import { useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import api from "@/api";
import { Lock, Unlock, MailQuestion } from "lucide-react";
import { toast } from "sonner";

export const LockGuard = ({ section, children }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [unlocked, setUnlocked] = useState(sessionStorage.getItem("cc_unlocked") === "1");
  const [mode, setMode] = useState("pin");
  const [code, setCode] = useState("");
  const [newPin, setNewPin] = useState("");
  const [hint, setHint] = useState("");

  const isLocked = user?.has_lock && (user?.locked_sections || []).includes(section) && !unlocked;
  if (!isLocked) return children;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.unlockSections(pin);
      sessionStorage.setItem("cc_unlocked", "1");
      setUnlocked(true);
      toast.success(t("unlockedOk"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("wrongPin"));
      setPin("");
    } finally { setBusy(false); }
  };

  const forgot = async () => {
    setBusy(true);
    try {
      const r = await api.forgotPin();
      setHint(r.email_hint || "");
      setMode("reset");
      toast.success(t("pinCodeSent"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("genericError"));
    } finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.resetPin(code.trim(), newPin.trim());
      toast.success(t("pinResetOk"));
      setMode("pin");
      setPin("");
      setCode("");
      setNewPin("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("genericError"));
    } finally { setBusy(false); }
  };

  if (mode === "reset") {
    return (
      <div className="p-8 flex items-center justify-center min-h-[70vh]" data-testid="lock-guard-reset">
        <form onSubmit={doReset} className="card-earth p-8 max-w-sm w-full text-center">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4"><MailQuestion className="w-7 h-7" /></div>
          <div className="serif text-2xl font-semibold mb-1">{t("pinResetTitle")}</div>
          <p className="text-sm text-muted-foreground mb-5">{t("pinCodeSent")}{hint ? ` (${hint})` : ""}</p>
          <input
            type="text" inputMode="numeric" maxLength={6}
            className="field w-full text-center text-xl tracking-[0.4em] mono mb-3"
            placeholder={t("pinResetCodePh")} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            data-testid="pin-reset-code-input"
          />
          <input
            type="password" inputMode="numeric" maxLength={8}
            className="field w-full text-center text-xl tracking-[0.4em] mono mb-4"
            placeholder={t("pinNewPh")} value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
            data-testid="pin-reset-new-input"
          />
          <button type="submit" disabled={busy || code.length < 6 || newPin.length < 4} className="btn-primary w-full text-sm" data-testid="pin-reset-submit">
            {busy ? "…" : t("pinResetBtn")}
          </button>
          <button type="button" onClick={() => setMode("pin")} className="text-xs text-muted-foreground hover:text-foreground mt-3" data-testid="pin-reset-back">
            ← {t("back")}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="p-8 flex items-center justify-center min-h-[70vh]" data-testid="lock-guard">
      <form onSubmit={submit} className="card-earth p-8 max-w-sm w-full text-center">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4"><Lock className="w-7 h-7" /></div>
        <div className="serif text-2xl font-semibold mb-1">{t("lockedSectionTitle")}</div>
        <p className="text-sm text-muted-foreground mb-5">{t("lockedSectionSub")}</p>
        <input
          type="password" inputMode="numeric" pattern="\d*" maxLength={8} autoFocus
          className="field w-full text-center text-2xl tracking-[0.5em] mono mb-4"
          placeholder="••••" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          data-testid="lock-pin-input"
        />
        <button type="submit" disabled={busy || pin.length < 4} className="btn-primary w-full text-sm" data-testid="lock-unlock-btn">
          <Unlock className="w-4 h-4" /> {busy ? "…" : t("unlockBtn")}
        </button>
        <button type="button" onClick={forgot} disabled={busy} className="text-xs text-primary font-medium link-underline mt-3" data-testid="lock-forgot-pin">
          {t("forgotPinLink")}
        </button>
      </form>
    </div>
  );
};
